import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { ShopState, ShopContextType, PowerupType } from '@/types/game';
import { ShopManager, SHOP_POWERUPS } from '@/game/shopManager';
import type { ShopPowerup } from '@/game/shopManager';
import type { PropEffect } from '@/types/props';

// ShopContext implementation using the proper ShopManager with LifeManager integration

interface ExtendedShopContextType extends ShopContextType {
  shopManager: ShopManager;
  getTimeUntilNextLife: () => number;
  getFormattedTimeUntilNextLife: () => string;
  purchasePowerup: (type: PowerupType) => boolean;
  shopPowerups: Record<PowerupType, ShopPowerup>;
  hasShield: () => boolean;
  hasInvincibility: () => boolean;
  purchaseProp: (propId: string) => boolean;
  equipProp: (propId: string) => boolean;
  unequipProp: (propId: string) => boolean;
  getEquippedPropEffects: () => PropEffect[];
  purchaseCharacter: (characterId: string, price: number, currency: 'coins' | 'gems') => boolean;
  equipCharacter: (characterId: string) => boolean;
}

const ShopContext = createContext<ExtendedShopContextType | undefined>(undefined);

interface ShopProviderProps {
  children: ReactNode;
}

export function ShopProvider({ children }: ShopProviderProps) {
  const shopManagerRef = useRef<ShopManager | null>(null);
  const [shopState, setShopState] = useState(() => {
    const manager = new ShopManager();
    shopManagerRef.current = manager;
    // Expose on window for legacy compatibility
    (window as any).shopManager = manager;
    return manager.getShopState();
  });

  // Update state when shop manager changes
  const updateInternalState = useCallback(() => {
    if (shopManagerRef.current) {
      const newState = shopManagerRef.current.getShopState();
      setShopState(newState);
    }
  }, []);

  // Subscribe to shop manager updates
  useEffect(() => {
    if (shopManagerRef.current) {
      const unsubscribe = shopManagerRef.current.onUpdate(() => {
        updateInternalState();
      });
      return unsubscribe;
    }
  }, [updateInternalState]);

  // Get current shop manager
  const shopManager = shopManagerRef.current!;

  const updateShopState = useCallback((updates: Partial<ShopState>) => {
    if (!shopManager) return;
    
    if (updates.coins !== undefined) {
      const diff = updates.coins - shopState.coins;
      if (diff > 0) {
        shopManager.addCoins(diff);
      } else if (diff < 0) {
        shopManager.spendCoins(-diff);
      }
    }
    
    if (updates.gems !== undefined) {
      const diff = updates.gems - shopState.gems;
      if (diff > 0) {
        shopManager.addGems(diff);
      } else if (diff < 0) {
        shopManager.spendGems(-diff);
      }
    }
    
    if (updates.lives !== undefined) {
      shopManager.setLives(updates.lives);
    }
    
    // Update state after changes
    updateInternalState();
  }, [shopManager, shopState.coins, shopState.gems, updateInternalState]);

  const purchasePowerUp = useCallback((powerUpId: string): boolean => {
    if (!shopManager) return false;
    
    const result = shopManager.purchasePowerup(powerUpId as PowerupType);
    if (result) {
      updateInternalState();
    }
    return result;
  }, [shopManager, updateInternalState]);

  const activatePowerUp = useCallback((powerUpId: string): boolean => {
    if (!shopManager) return false;
    
    const success = shopManager.usePowerup(powerUpId as PowerupType);
    if (success) {
      updateInternalState();
    }
    return success;
  }, [shopManager, updateInternalState]);

  const addCoins = useCallback((amount: number) => {
    if (!shopManager) return;
    shopManager.addCoins(amount);
    updateInternalState();
  }, [shopManager, updateInternalState]);

  const addGems = useCallback((amount: number) => {
    if (!shopManager) return;
    shopManager.addGems(amount);
    updateInternalState();
  }, [shopManager, updateInternalState]);

  const getTimeUntilNextLife = useCallback((): number => {
    if (!shopManager) return 0;
    return shopManager.getTimeUntilNextLife();
  }, [shopManager]);

  const getFormattedTimeUntilNextLife = useCallback((): string => {
    if (!shopManager) return '';
    return shopManager.getFormattedTimeUntilNextLife();
  }, [shopManager]);

  const purchasePowerup = useCallback((type: PowerupType): boolean => {
    if (!shopManager) return false;
    return shopManager.purchasePowerup(type);
  }, [shopManager]);

  const hasShield = useCallback((): boolean => {
    if (!shopManager) return false;
    return shopManager.hasPowerup('shield');
  }, [shopManager]);

  const hasInvincibility = useCallback((): boolean => {
    if (!shopManager) return false;
    return shopManager.hasPowerup('invincibility');
  }, [shopManager]);

  const purchaseProp = useCallback((propId: string): boolean => {
    if (!shopManager) return false;
    return shopManager.purchaseProp(propId);
  }, [shopManager]);

  const equipProp = useCallback((propId: string): boolean => {
    if (!shopManager) return false;
    return shopManager.equipProp(propId);
  }, [shopManager]);

  const unequipProp = useCallback((propId: string): boolean => {
    if (!shopManager) return false;
    return shopManager.unequipProp(propId);
  }, [shopManager]);

  const getEquippedPropEffects = useCallback(() => {
    if (!shopManager) return [];
    return shopManager.getEquippedPropEffects();
  }, [shopManager]);

  const purchaseCharacter = useCallback((characterId: string, price: number, currency: 'coins' | 'gems'): boolean => {
    if (!shopManager) return false;
    return shopManager.purchaseCharacter(characterId, price, currency);
  }, [shopManager]);

  const equipCharacter = useCallback((characterId: string): boolean => {
    if (!shopManager) return false;
    return shopManager.equipCharacter(characterId);
  }, [shopManager]);

  const contextValue: ExtendedShopContextType = useMemo(() => ({
    shopState,
    updateShopState,
    purchasePowerUp,
    activatePowerUp,
    addCoins,
    addGems,
    shopManager: shopManager!,
    getTimeUntilNextLife,
    getFormattedTimeUntilNextLife,
    purchasePowerup,
    shopPowerups: SHOP_POWERUPS,
    hasShield,
    hasInvincibility,
    purchaseProp,
    equipProp,
    unequipProp,
    getEquippedPropEffects,
    purchaseCharacter,
    equipCharacter,
  }), [
    shopState,
    updateShopState,
    purchasePowerUp,
    activatePowerUp,
    addCoins,
    addGems,
    shopManager,
    getTimeUntilNextLife,
    getFormattedTimeUntilNextLife,
    purchasePowerup,
    hasShield,
    hasInvincibility,
    purchaseProp,
    equipProp,
    unequipProp,
    getEquippedPropEffects,
    purchaseCharacter,
    equipCharacter,
  ]);

  return (
    <ShopContext.Provider value={contextValue}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
}